import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ROLE_COLORS = {
  chairman: 'bg-purple-100 text-purple-700',
  vice_chairman: 'bg-blue-100 text-blue-700',
  secretary: 'bg-green-100 text-green-700',
  treasurer: 'bg-yellow-100 text-yellow-700',
  member: 'bg-gray-100 text-gray-600',
};

export default function MemberCommittee() {
  const [committees, setCommittees] = useState([]);
  const [myMemberships, setMyMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState('my'); // 'my' | 'all'

  const fetchCommittees = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/committees/'),
      api.get('/committees/my/'),
    ])
      .then(([allRes, myRes]) => {
        setCommittees(allRes.data.results || allRes.data);
        setMyMemberships(myRes.data.results || myRes.data);
      })
      .catch(() => toast.error('Failed to load committees'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCommittees(); }, [fetchCommittees]);

  const viewDetail = async (c) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/committees/${c.id}/`);
      setSelectedCommittee(r.data);
    } catch { toast.error('Failed to load committee details'); }
    finally { setDetailLoading(false); }
  };

  const filtered = committees.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedCommittee) {
    return (
      <div className="space-y-6">
        <button onClick={() => setSelectedCommittee(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Committees
        </button>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-xl font-bold text-gray-800">{selectedCommittee.name}</h2>
          {selectedCommittee.description && <p className="text-sm text-gray-500 mt-1">{selectedCommittee.description}</p>}
          <p className="text-xs text-gray-400 mt-2">
            <span className="font-semibold text-[#0B5D1E]">{selectedCommittee.member_count}</span> member{selectedCommittee.member_count !== 1 ? 's' : ''}
          </p>
        </div>

        {(selectedCommittee.members_list || []).length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <p className="text-gray-400">No members in this committee.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">S/N</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Membership #</th>
                  <th className="px-4 py-3 text-left">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {selectedCommittee.members_list.map((m, idx) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{m.member_name}</td>
                    <td className="px-4 py-3 text-gray-600">{m.membership_number || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-600'}`}>
                        {m.role_display || m.role}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Committees</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button onClick={() => setTab('my')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === 'my' ? 'border-[#0B5D1E] text-[#0B5D1E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          My Committees {myMemberships.length > 0 && <span className="ml-1 bg-[#0B5D1E] text-white text-xs rounded-full px-2 py-0.5">{myMemberships.length}</span>}
        </button>
        <button onClick={() => setTab('all')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === 'all' ? 'border-[#0B5D1E] text-[#0B5D1E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          All Committees
        </button>
      </div>

      {/* My Committees Tab */}
      {tab === 'my' && (
        <>
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading...</p>
            </div>
          ) : myMemberships.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-500 mb-2">No Committee Memberships</h2>
              <p className="text-gray-400">You have not been added to any committee yet.</p>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {myMemberships.map((m, idx) => (
                <div key={m.id} className="bg-white rounded-xl shadow-sm border border-[#0B5D1E]/20 p-5 hover:shadow-md transition relative cursor-pointer"
                  onClick={() => viewDetail({ id: m.committee_id })}>
                  <div className="absolute top-3 left-3 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</div>
                  <div className="ml-8 space-y-2">
                    <h3 className="font-semibold text-gray-800">{m.committee_name}</h3>
                    {m.committee_description && <p className="text-xs text-gray-500 line-clamp-2">{m.committee_description}</p>}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-600'}`}>
                        {m.role_display}
                      </span>
                      <span className="text-xs text-gray-400">Joined: {m.joined_date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* All Committees Tab */}
      {tab === 'all' && (
        <>
      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input type="text" placeholder="Search committees..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>

      {loading || detailLoading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Committees</h2>
          <p className="text-gray-400">There are no active committees at the moment.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c, idx) => (
            <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition relative cursor-pointer"
              onClick={() => viewDetail(c)}>
              <div className="absolute top-3 left-3 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</div>
              <div className="ml-8 space-y-2">
                <h3 className="font-semibold text-gray-800">{c.name}</h3>
                {c.description && <p className="text-xs text-gray-500 line-clamp-2">{c.description}</p>}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    <span className="font-semibold text-[#0B5D1E]">{c.member_count}</span> member{c.member_count !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-[#0B5D1E] font-semibold">View Members →</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
}
